// Shared config builder for tamagui.config.ts (native) and
// tamagui.config.web.ts (web) -- everything EXCEPT the animations driver,
// which is the one piece that must differ per platform (see both entry
// files' own comments for why). Metro/Expo's platform-extension module
// resolution (`.web.ts` vs `.ts`) picks the right entry file for
// `import tamaguiConfig from '../../tamagui.config'` (src/app/_layout.tsx)
// at actual bundle time, which is what makes this split work: each
// platform's bundle only ever imports (and therefore only ever
// evaluates/executes) the animations driver it's actually meant to run,
// rather than both drivers' module-scope side effects loading
// unconditionally into one shared file regardless of a runtime check.
//
// Built on @tamagui/config's own default config rather than hand-rolled
// fonts/media -- a hand-rolled font setup broke createTamagui with an
// opaque "Cannot convert undefined or null to object" (caught by actually
// rendering the app, not by reasoning about the config in the abstract),
// and Tamagui's own defaults are the better-supported, less-code path per
// this project's "rely on the ecosystem, don't hand-roll" principle. Only
// color/space/radius/theme are overridden with the real design tokens;
// fonts/media stay default.
//
// light/dark themes below use the SAME semantic keys with different
// values -- this preserves the token kit's primitive -> semantic ->
// dark-override structure (see tokens-and-color.md's Dark Mode
// Strategy) instead of flattening to resolved hex, so a component
// never hardcodes a color and dark mode is a theme swap, not a
// per-component rewrite.
import { config as defaultConfig } from '@tamagui/config';
import { createFont, createTamagui, createTokens } from 'tamagui';
import { darkColors, fontSize, fontWeight, lightColors, lineHeight, radius, space } from './src/theme/tokens.generated';

// Pulled from `createTamagui`'s OWN parameter type, not from
// `typeof defaultConfig.animations` -- the latter narrows to that
// object's exact preset keys ('0ms'/'50ms'/.../tooltip), which the
// Reanimated driver's different preset names (quick/medium/lazy/bouncy)
// then fail to satisfy. `createTamagui`'s real `animations` parameter
// type is intentionally generic over the preset-name shape, which is
// what both drivers actually need to be checked against.
type AnimationsConfig = Parameters<typeof createTamagui>[0]['animations'];

// Real brand fonts (Fraunces / Libre Franklin), matching the landing
// page's <link> and tokens/typography.json -- loaded via expo-font in
// src/app/_layout.tsx (RootLayout), which is what makes these family
// names resolve to something other than a fallback. `face` maps each
// numeric weight to the specific loaded file for that weight (RN/web
// custom fonts are one file per weight, not one file with CSS
// font-weight synthesis); `family` is just the default/fallback.
//
// Seven role-named steps (tokens/typography.json): `fontSize="$body"`,
// `"$meta"`, `"$caption"`, `"$titleS"`, `"$title"`, `"$headline"`,
// `"$display"`. Size AND line height come from the same role key, so a
// token reference can never pair a size with the wrong leading. `true`
// (every unsized <Paragraph>) is body. Numeric `$1`-`$16` aliases are gone:
// they collided with the spacing keys and hid which size a call site meant.
const bodyFontSize = { ...fontSize, true: fontSize.body } as const;
const bodyLineHeight = { ...lineHeight, true: lineHeight.body } as const;

const headingFont = createFont({
  ...defaultConfig.fonts.heading,
  family: 'Fraunces_400Regular',
  face: {
    400: { normal: 'Fraunces_400Regular' },
    600: { normal: 'Fraunces_600SemiBold' },
    700: { normal: 'Fraunces_700Bold' },
    800: { normal: 'Fraunces_700Bold' },
  },
  // See bodyFontSize's own comment -- `Heading`/`Subheading` set their
  // own explicit px fontSize and don't depend on this scale, but any
  // OTHER text rendered with `fontFamily="$heading"` plus a numeric
  // `fontSize="$N"` (e.g. preview.tsx's screen-name text) should track
  // the same real scale as body text, not @tamagui/config's default
  // (whose heading scale oddly dips at keys 5-6, a quirk tuned for a
  // variable optical-size font this app doesn't use).
  size: bodyFontSize,
  lineHeight: bodyLineHeight,
  weight: fontWeight,
});

const bodyFont = createFont({
  ...defaultConfig.fonts.body,
  family: 'LibreFranklin_400Regular',
  face: {
    400: { normal: 'LibreFranklin_400Regular' },
    500: { normal: 'LibreFranklin_500Medium' },
    600: { normal: 'LibreFranklin_600SemiBold' },
    700: { normal: 'LibreFranklin_700Bold' },
  },
  size: bodyFontSize,
  lineHeight: bodyLineHeight,
  weight: fontWeight,
});

// @tamagui/config's default fonts only define heading/body -- mono is
// the landing page's third family (labels, buttons, the wordmark), so
// it's built the same way, off the body scale (mono has no display-size
// use case here, unlike heading).
const monoFont = createFont({
  ...defaultConfig.fonts.body,
  weight: fontWeight,
  family: 'SpaceMono_400Regular',
  face: {
    400: { normal: 'SpaceMono_400Regular' },
    700: { normal: 'SpaceMono_700Bold' },
  },
});

// NOTE: `size` is deliberately NOT overridden here. An earlier version
// of this file set `size: { ...space, true: space['4'] }`, which
// replaced Tamagui's control-size scale (default $4/$true = 44, tuned
// for touch targets) with the 4px spacing scale (space['4'] = 16) --
// every size-driven component (Button, Input, Switch...) silently
// rendered ~16px instead of ~44px, below the WCAG 2.5.8 24x24 minimum.
// Caught by adversarial review, verified against @tamagui/config's
// actual default in node_modules, not assumed. `size` stays whatever
// `...defaultConfig.tokens` already provides.
const tokens = createTokens({
  ...defaultConfig.tokens,
  color: { ...lightColors, ...Object.fromEntries(Object.entries(darkColors).map(([k, v]) => [`${k}Dark`, v])) },
  space: { ...space, true: space['4'] },
  radius: { ...radius, true: radius.md },
});

// Semantic theme keys: the same names in both themes, values swapped.
// This is the exact pairing checked by the "flip color scheme, confirm
// app-wide swap with zero component edits" verification in issue #11.
const light = {
  background: lightColors.surfacePage,
  backgroundStrong: lightColors.surfaceRaised,
  backgroundHover: lightColors.interactiveHoverOverlay,
  backgroundPress: lightColors.interactiveActiveOverlay,
  backgroundFocus: lightColors.interactiveHoverOverlay,
  card: lightColors.surfaceCard,
  color: lightColors.textPrimary,
  colorSecondary: lightColors.textSecondary,
  colorTertiary: lightColors.textTertiary,
  colorLink: lightColors.textLink,
  colorLinkHover: lightColors.textLinkHover,
  colorOnAction: lightColors.textOnAction,
  colorPress: lightColors.textPrimary,
  colorFocus: lightColors.textPrimary,
  colorHover: lightColors.textPrimary,
  borderColor: lightColors.borderDefault,
  borderColorHover: lightColors.borderStrong,
  borderColorPress: lightColors.borderStrong,
  borderColorFocus: lightColors.borderFocus,
  borderColorStrong: lightColors.borderStrong,
  primary: lightColors.actionPrimary,
  // `$name` resolves theme keys first, then the plain color tokens (which are
  // always the LIGHT values). These four were used as `$name` without a theme
  // key, so they stayed light in dark mode (prompt cards, chips).
  actionPrimary: lightColors.actionPrimary,
  interactiveSelectedBg: lightColors.interactiveSelectedBg,
  interactiveSelectedBorder: lightColors.interactiveSelectedBorder,
  surfaceRaised: lightColors.surfaceRaised,
  primaryHover: lightColors.actionPrimaryHover,
  primaryActive: lightColors.actionPrimaryActive,
  secondary: lightColors.actionSecondary,
  secondaryHover: lightColors.actionSecondaryHover,
  destructive: lightColors.actionDestructive,
  destructiveHover: lightColors.actionDestructiveHover,
  // Issue #148 AC1: @tamagui/button's own default `focusVisibleStyle`
  // already reaches for `$outlineColor` (confirmed by reading
  // node_modules/@tamagui/button/dist/esm/Button.mjs directly --
  // `focusVisibleStyle: { outlineColor: "$outlineColor", outlineStyle:
  // "solid", outlineWidth: 2 }`), but this theme never defined that
  // token -- the exact source of the "[tamagui] Warning: missing token
  // outlineColor in category color" warning seen in every dev-server
  // log this session, previously unexplained. `$primary`, not a new
  // color: measured (not assumed) 7.55:1 against light surfacePage and
  // 3.17:1 against dark surfacePage, both clearing the WCAG 3:1 UI-
  // component/focus-indicator floor -- see this token's own duplicate
  // in the `dark` theme below for the matching dark-mode value. Fixing
  // this at the THEME level (not just inside PrimaryButton's own
  // styled() config) means every Tamagui component that already relies
  // on this same built-in mechanism gets a real, on-brand focus ring
  // for free, not just this one button.
  outlineColor: lightColors.actionPrimary,
  // The pseudonymous-avatar palette (lib/avatar-identity.ts) reused
  // these five bg/text pairs as bare $badge* token-scale references
  // rather than real theme keys -- harmless today only because
  // lightColors/darkColors happen to define identical values for every
  // badge* entry, but it means the avatar palette silently bypasses
  // the theme layer: the moment either set gets a real dark-specific
  // value, avatars would stop following it with no failing gate to
  // catch it (final review, PR #52). Adding them here, the same way
  // every other semantic color above is, makes the theme the actual
  // source of truth for these colors, per this project's "one theme,
  // one source of truth" rule.
  badgeNeutralBg: lightColors.badgeNeutralBg,
  badgeNeutralText: lightColors.badgeNeutralText,
  badgePrimaryBg: lightColors.badgePrimaryBg,
  badgePrimaryText: lightColors.badgePrimaryText,
  badgeSuccessBg: lightColors.badgeSuccessBg,
  badgeSuccessText: lightColors.badgeSuccessText,
  badgeWarningBg: lightColors.badgeWarningBg,
  badgeWarningText: lightColors.badgeWarningText,
  badgeErrorBg: lightColors.badgeErrorBg,
  badgeErrorText: lightColors.badgeErrorText,
  // Semantic feedback callouts (safety-reminder screen, block/report
  // outcome states) -- distinct from the badge* palette above, which
  // is decorative/categorical only (avatar identity colors, explicitly
  // never a status signal per that block's own comment). These ARE a
  // status signal: warning for the safety reminder, error for a
  // destructive-outcome confirmation, success for "this worked".
  feedbackWarningBg: lightColors.feedbackWarningBg,
  feedbackWarningText: lightColors.feedbackWarningText,
  feedbackWarningBorder: lightColors.feedbackWarningBorder,
  feedbackWarningIcon: lightColors.feedbackWarningIcon,
  feedbackErrorBg: lightColors.feedbackErrorBg,
  feedbackErrorText: lightColors.feedbackErrorText,
  feedbackErrorBorder: lightColors.feedbackErrorBorder,
  feedbackErrorIcon: lightColors.feedbackErrorIcon,
  feedbackSuccessBg: lightColors.feedbackSuccessBg,
  feedbackSuccessText: lightColors.feedbackSuccessText,
  feedbackSuccessBorder: lightColors.feedbackSuccessBorder,
  feedbackSuccessIcon: lightColors.feedbackSuccessIcon,
  // Added for Toast's "info" variant (issue #150 Task 4) -- the raw
  // tokens already existed in tokens.generated.ts (tokens/colors.json's
  // own 4-variant feedback set), but were never wired into either theme
  // object, unlike Warning/Error/Success above. No component reached
  // for `$feedbackInfo*` before Toast, so the gap was latent, not live.
  feedbackInfoBg: lightColors.feedbackInfoBg,
  feedbackInfoText: lightColors.feedbackInfoText,
  feedbackInfoBorder: lightColors.feedbackInfoBorder,
  feedbackInfoIcon: lightColors.feedbackInfoIcon,
};

const dark = {
  background: darkColors.surfacePage,
  backgroundStrong: darkColors.surfaceRaised,
  backgroundHover: darkColors.interactiveHoverOverlay,
  backgroundPress: darkColors.interactiveActiveOverlay,
  backgroundFocus: darkColors.interactiveHoverOverlay,
  card: darkColors.surfaceCard,
  color: darkColors.textPrimary,
  colorSecondary: darkColors.textSecondary,
  colorTertiary: darkColors.textTertiary,
  colorLink: darkColors.textLink,
  colorLinkHover: darkColors.textLinkHover,
  colorOnAction: darkColors.textOnAction,
  colorPress: darkColors.textPrimary,
  colorFocus: darkColors.textPrimary,
  colorHover: darkColors.textPrimary,
  borderColor: darkColors.borderDefault,
  borderColorHover: darkColors.borderStrong,
  borderColorPress: darkColors.borderStrong,
  borderColorFocus: darkColors.borderFocus,
  borderColorStrong: darkColors.borderStrong,
  primary: darkColors.actionPrimary,
  // `$name` resolves theme keys first, then the plain color tokens (which are
  // always the LIGHT values). These four were used as `$name` without a theme
  // key, so they stayed light in dark mode (prompt cards, chips).
  actionPrimary: darkColors.actionPrimary,
  interactiveSelectedBg: darkColors.interactiveSelectedBg,
  interactiveSelectedBorder: darkColors.interactiveSelectedBorder,
  surfaceRaised: darkColors.surfaceRaised,
  primaryHover: darkColors.actionPrimaryHover,
  primaryActive: darkColors.actionPrimaryHover,
  secondary: darkColors.actionSecondary,
  secondaryHover: darkColors.actionSecondaryHover,
  destructive: darkColors.actionDestructive,
  destructiveHover: darkColors.actionDestructiveHover,
  // See the `light` theme's own outlineColor comment above -- same
  // reasoning.
  outlineColor: darkColors.actionPrimary,
  // See the `light` theme's own comment above -- same reasoning.
  badgeNeutralBg: darkColors.badgeNeutralBg,
  badgeNeutralText: darkColors.badgeNeutralText,
  badgePrimaryBg: darkColors.badgePrimaryBg,
  badgePrimaryText: darkColors.badgePrimaryText,
  badgeSuccessBg: darkColors.badgeSuccessBg,
  badgeSuccessText: darkColors.badgeSuccessText,
  badgeWarningBg: darkColors.badgeWarningBg,
  badgeWarningText: darkColors.badgeWarningText,
  badgeErrorBg: darkColors.badgeErrorBg,
  badgeErrorText: darkColors.badgeErrorText,
  // See the `light` theme's own comment above.
  feedbackWarningBg: darkColors.feedbackWarningBg,
  feedbackWarningText: darkColors.feedbackWarningText,
  feedbackWarningBorder: darkColors.feedbackWarningBorder,
  feedbackWarningIcon: darkColors.feedbackWarningIcon,
  feedbackErrorBg: darkColors.feedbackErrorBg,
  feedbackErrorText: darkColors.feedbackErrorText,
  feedbackErrorBorder: darkColors.feedbackErrorBorder,
  feedbackErrorIcon: darkColors.feedbackErrorIcon,
  feedbackSuccessBg: darkColors.feedbackSuccessBg,
  feedbackSuccessText: darkColors.feedbackSuccessText,
  feedbackSuccessBorder: darkColors.feedbackSuccessBorder,
  feedbackSuccessIcon: darkColors.feedbackSuccessIcon,
  // See the `light` theme's own comment above.
  feedbackInfoBg: darkColors.feedbackInfoBg,
  feedbackInfoText: darkColors.feedbackInfoText,
  feedbackInfoBorder: darkColors.feedbackInfoBorder,
  feedbackInfoIcon: darkColors.feedbackInfoIcon,
};

// Component sub-themes: Tamagui looks up `${themeName}_${componentName}`
// (componentName comes from the component's own `styled({ name: ... })`,
// e.g. @tamagui/input's Input/TextArea) before falling back to the base
// theme -- this is the supported mechanism for overriding ONE
// component's look app-wide without touching every call site. Used
// here to fix Input/TextArea's inherited `borderColor: "$borderColor"`
// default: `$borderColor` resolves to border.default (1.27:1 light /
// 1.44:1 dark -- decorative-only per tokens/colors.json's own
// "sub-3:1 by design" comment on that token), correct for the dividers
// it's meant for but wrong for an essential control boundary (an input
// box, per this project's own tokens-and-color.md -- "essential
// control borders" want border.strong, >=3:1 UI contrast). Overriding
// just `borderColor` here, scoped to Input/TextArea's own sub-theme,
// fixes every input/textarea app-wide while leaving every OTHER use of
// `$borderColor` (dividers, card edges) at its correct decorative
// value -- confirmed by rendering both themes (see task-8-report.md).
const inputSubTheme = { borderColor: lightColors.borderStrong };
const inputSubThemeDark = { borderColor: darkColors.borderStrong };

// Spread over the default themes rather than replacing them -- the
// defaults carry 130+ keys (color1-12, placeholderColor, shadowColor*,
// backgroundTransparent, sub-themes like "blue"/"red"...) that Tamagui's
// own built-in components reach for. Replacing wholesale (as an earlier
// version of this file did) left those `undefined` for any component
// that used them -- caught by adversarial review, not by rendering,
// since the visible screens so far don't exercise most of those keys.
export function createAppConfig(animations: AnimationsConfig) {
  const config = createTamagui({
    ...defaultConfig,
    tokens,
    animations,
    themes: {
      ...defaultConfig.themes,
      light: { ...defaultConfig.themes.light, ...light },
      dark: { ...defaultConfig.themes.dark, ...dark },
      light_Input: inputSubTheme,
      dark_Input: inputSubThemeDark,
      light_TextArea: inputSubTheme,
      dark_TextArea: inputSubThemeDark,
    },
    fonts: { heading: headingFont, body: bodyFont, mono: monoFont },
    // NOTE: radius and the web CSS transition anti-pattern fix for
    // Input/TextArea do NOT live here as `defaultProps` -- that's the
    // config-level mechanism Tamagui exposes for it, but passing it
    // through this SAME object literal broke shorthand prop inference
    // (`px`, `pb`, ...) across every unrelated screen in the app: adding
    // `defaultProps` here made `createTamagui`'s generic `Conf` fail to
    // structurally match `ConfProps<...>`, which silently degrades
    // `TamaguiCustomConfig`'s inferred shorthands to `unknown` --
    // verified by adding just that one key and re-running `tsc --noEmit`
    // (it broke ~10 files with zero relation to Input/TextArea), then
    // reverting it and confirming the same errors disappeared. Input's
    // sharp radius + scoped transition are set instead via a small
    // styled() wrapper (src/components/input.tsx, src/components/
    // text-area.tsx) at the only 3 call sites that use them directly --
    // see those files for the actual fix.
  });

  return config;
}
